import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../env';
import { AppLogger, AppLoggerService } from '../../core/logging';
import { RecipeDraftPayload } from './dialog.models';

// Same wire shape as the URL importer's own job draft (both are backed by the
// same "Pantry Recipe Format" on the backend, see internal/domain/recipe/format.go)
// — see RecipeImportService's RecipeDraftDto for the identical reasoning.
export type RecipeAuthorJobDraftDto = Omit<RecipeDraftPayload, 'recipe_id'>;

export type RecipeAuthorJobDto = {
  id: string;
  owner_user_id?: number;
  household_id?: number;
  created_recipe_id?: number;
  status?: 'running' | 'draft_ready' | 'succeeded' | 'failed' | string;
  state_message?: string;
  // Populated once status reaches 'draft_ready': the authored recipe, held
  // for review — nothing is persisted until saveJob() is called.
  draft?: RecipeAuthorJobDraftDto | null;
  created_at?: string;
  updated_at?: string;

  // allow backend to evolve
  [key: string]: any;
};

export type RecipeAuthorJobResponse = {
  job: RecipeAuthorJobDto;
};

/**
 * Polls and saves the dialog assistant's background recipe-authoring jobs
 * (backend: internal/app/recipeauthor). There is no "start" method here — a
 * job is registered server-side when the model calls propose_recipe, never
 * directly from the client; the turn response that started it carries the
 * job id (`DialogTurnResponse.recipe_job.job_id`).
 */
@Injectable({ providedIn: 'root' })
export class RecipeAuthorJobService {
  private readonly log: AppLogger;

  constructor(
    private http: HttpClient,
    private appLogger: AppLoggerService
  ) {
    this.log = this.appLogger.createForClass(RecipeAuthorJobService);
  }

  /**
   * Poll a recipe-authoring job's status.
   * GET /api/households/:householdId/dialog/recipe-jobs/:jobId
   */
  getJob(householdId: number, jobId: string): Observable<RecipeAuthorJobResponse> {
    this.log.debug('Get recipe author job', { householdId, jobId });

    return this.http.get<RecipeAuthorJobResponse>(
      `${environment.apiUrl}/households/${householdId}/dialog/recipe-jobs/${jobId}`
    );
  }

  /**
   * Persist a `draft_ready` job's recipe as a real, household-scoped recipe.
   * POST /api/households/:householdId/dialog/recipe-jobs/:jobId/save
   */
  saveJob(householdId: number, jobId: string): Observable<RecipeAuthorJobResponse> {
    this.log.debug('Save recipe author job', { householdId, jobId });

    return this.http.post<RecipeAuthorJobResponse>(
      `${environment.apiUrl}/households/${householdId}/dialog/recipe-jobs/${jobId}/save`,
      {}
    );
  }
}
